import { useContext, useEffect } from "react";
import ListItem from "@mui/material/ListItem";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import SelectInput from "../SelectInput";
import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { appendRestAndWebhookFields, appendSftpFields } from "./appendFields";
import { useSelector } from "react-redux";
import AutocompleteInput from "../AutocompleteInput";

const fieldsToPlaceMenuAtBottom = ["method", "operation"];

const DropdownListItem = ({ selectedNode, action, fieldValue, allowEditWorkflow }) => {
   const { actionForm, componentAction, formErrors } = useContext(ComponentPropsContext);
   const { setActionForm, setComponentAction, setFormErrors, filterValidationErrors } = useContext(ComponentPropsContextApi);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const isFlowActive = currentSavedFlow?.shared_status === "active";

   const options = action.options.map((entry) => {
      if (typeof entry === "object") {
         return {
            value: entry.value,
            label: entry.text,
            isDisabled: entry.disabled
         };
      }
      return {
         value: entry,
         label: entry
      };
   });

   const handleDropdownChange = (e, item) => {
      const { name } = item;
      let copy = { ...actionForm };

      copy = { ...actionForm, [name]: { ...actionForm[name], value: e.value } };

      // MCP bearer credentials remain in the existing credential vault. OAuth
      // connections are managed separately, so they must not render that picker.
      if (selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && name === "authenticationType") {
         copy.credentialRequired = {
            ...actionForm.credentialRequired,
            value: e.value === "bearerToken"
         };

         if (e.value !== "oauth") {
            delete copy.oauthConnectionId;
         }
      }

      setActionForm(copy);

      // If the selected node is REST API and the selected field is authenticationType, then add the fields based on the selected value
      if (selectedNode.key === COMMON_COMPONENT_TYPES.REST_API || selectedNode.key === COMMON_COMPONENT_TYPES.WEBHOOK) {
         appendRestAndWebhookFields(name, e.value, copy, setActionForm, componentAction, setComponentAction);
      } else if (selectedNode.key === COMMON_COMPONENT_TYPES.SFTP) {
         appendSftpFields(name, e.value, copy, setActionForm, componentAction, setComponentAction);
      }

      setTimeout(() => {
         filterValidationErrors(action.field);
      }, 100);
   };

   const handleDropDownBlur = () => {
      if (action.isRequired && !fieldValue) {
         setFormErrors((prev) => [...prev, action.field]);
      } else {
         filterValidationErrors(action.field);
      }
   };

   const renderSecondaryInput = () => {
      return (
         <>
            <Box
               sx={{
                  width: "100%",
                  marginLeft: "8px",
                  marginTop: "19px",
                  display: "flex",
                  justifyContent: "space-between"
               }}
            >
               <Box sx={{ flexGrow: 1 }}>
                  <AutocompleteInput
                     action={{ ...action.secondaryInput, parentField: action.field }}
                     fieldValue={actionForm[action.field]?.secondaryInput?.value ?? ""}
                     onChange={(event) => {
                        setActionForm((prev) => ({
                           ...prev,
                           [action.field]: {
                              ...prev[action.field],
                              secondaryInput: {
                                 ...actionForm[action.field]?.secondaryInput,
                                 value: event.target.value
                              }
                           }
                        }));
                     }}
                     hasSecondaryInput={true}
                     disabled={isFlowActive || !allowEditWorkflow}
                  />
               </Box>
            </Box>
         </>
      );
   };

   useEffect(() => {
      // hide the node selection when selected flow is different
      if (action?.field === "nodeId") {
         action.isRequired = true;
      }
   }, [selectedNode, actionForm.payloadType, action, currentSavedFlow?.id]);

   return (
      <ListItem
         key={action.label}
         sx={{
            width: "100%",
            paddingTop: action.field === "operator" ? "16px" : 0,
            marginBottom: action.field === "payloadContentType" ? "16px" : 0,
            justifyContent: "space-between"
         }}
      >
         <Box display="flex" width="100%" height="100%">
            <Box
               sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column"
               }}
            >
               <SelectInput
                  options={options.filter((option) => option.label !== undefined && option.label !== null)}
                  action={action}
                  fieldValue={fieldValue}
                  handleChange={handleDropdownChange}
                  error={formErrors?.includes(action.field)}
                  onBlur={handleDropDownBlur}
                  menuShouldScrollIntoView={false}
                  menuPlacement={fieldsToPlaceMenuAtBottom.includes(action?.field) ? "bottom" : "auto"}
                  disabled={isFlowActive || !allowEditWorkflow}
               />
               {formErrors?.includes(action.field) && (
                  <div>
                     <FormHelperText error>{`${action.label} is required.`}</FormHelperText>
                  </div>
               )}
            </Box>
            {action.secondaryInput && renderSecondaryInput()}
         </Box>
      </ListItem>
   );
};

export default DropdownListItem;
